import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * True se l'offerta è certamente "free cancellation".
 *
 * Vincolo BE: `is_non_refundable` ha default `false` per tutti gli adapter
 * che non lo popolano esplicitamente, quindi NON è un segnale affidabile
 * di rimborsabilità. L'unica fonte di verità è una `cancellation_deadline`
 * futura (proxy del vecchio FE OTA): se valorizzata e non scaduta, l'utente
 * può cancellare gratis fino a quella data.
 *
 * Senza deadline → trattiamo come non-rimborsabile (no false positives).
 */
export function isRefundable(offer?: {
  cancellation_deadline?: string | null;
  is_non_refundable?: boolean;
} | null): boolean {
  if (!offer) return false;
  if (offer.cancellation_deadline) {
    const dl = new Date(offer.cancellation_deadline);
    if (!Number.isNaN(dl.getTime()) && dl.getTime() > Date.now()) return true;
  }
  return false;
}

/** Formatta una cancellation_deadline ISO come "23 Mag" (it) o "23 May" (en). */
export function formatCxlDeadline(
  iso: string | null | undefined,
  locale = "it-IT",
): string | null {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return new Intl.DateTimeFormat(locale, {
      day: "numeric",
      month: "short",
    }).format(d);
  } catch {
    return null;
  }
}

export function formatCurrency(
  amount: number | string,
  currency: string,
  locale = "it-IT",
): string {
  const n = typeof amount === "string" ? parseFloat(amount) : amount;
  try {
    return new Intl.NumberFormat(locale, { style: "currency", currency }).format(n);
  } catch {
    return `${n.toFixed(2)} ${currency}`;
  }
}

export function formatDateRange(
  checkIn: string,
  checkOut: string,
  locale = "it-IT",
): string {
  const ci = new Date(checkIn);
  const co = new Date(checkOut);
  const fmt = new Intl.DateTimeFormat(locale, { day: "numeric", month: "short" });
  const nights = Math.round((co.getTime() - ci.getTime()) / 86400000);
  return `${fmt.format(ci)} — ${fmt.format(co)} · ${nights} ${nights === 1 ? "notte" : "notti"}`;
}

export function nightsBetween(checkIn: string, checkOut: string): number {
  const ci = new Date(checkIn).getTime();
  const co = new Date(checkOut).getTime();
  return Math.max(0, Math.round((co - ci) / 86400000));
}

/**
 * Ritorna `YYYY-MM-DD` nel **fuso orario locale** dell'utente, NON in UTC.
 *
 * `Date.prototype.toISOString()` formatta in UTC: per un utente IT
 * `new Date(2026, 4, 15) /* 15 maggio 00:00 locale * /.toISOString()` →
 * `"2026-05-14T22:00:00.000Z"`, e `slice(0, 10)` restituisce **il giorno
 * precedente**. È il classico off-by-one del calendario.
 *
 * Per i check-in/check-out delle prenotazioni serve la data **come la
 * vede l'utente**, quindi formattazione locale via getFullYear() ecc.
 */
export function isoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function addDays(date: Date, days: number): Date {
  const r = new Date(date);
  r.setDate(r.getDate() + days);
  return r;
}
